import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import Modal from './Modal'
import PatientSearchSelect from './PatientSearchSelect'

// ── Patient form modal (Create & Edit) ──────────────────────────────
// Phase 9.6 sub-area #4 — Patient Forms & Records.
//
// Single component handles both Create and Edit because the fields
// are identical; only endpoint + defaults differ.
//   - mode="create" → POST /patients
//   - mode="edit"   → PATCH /patients/:id
//
// The form is split into two visual sections:
//   1. Demographics — first/last name, phone, email, DOB, notes
//   2. Medical History — nested attributes on the medical_history
//      association. Everything here is optional so the receptionist
//      can capture a quick booking and fill records later.
//
// Phone validation mirrors the Rails model regex so errors surface
// client-side before a round-trip. Server-side validation is the
// source of truth — the redirect flow will surface any mismatch via
// the toast error handler.
// 🚨 PEOPLE TYPE PHONE NUMBERS WITH SPACES (Paul 2026-07-14). The old rule was /^\+?\d{10,15}$/ —
// digits ONLY — while the field's own placeholder demonstrates "+27 82 123 4567", WITH spaces. So typing
// the number exactly the way the form tells you to was rejected, the value was submitted as null, Rails
// saw nothing changed, and the app cheerfully redirected as if it had saved. Rudi Armstrong sat in the
// diary with no number and reception was told nothing.
// Now: strip the pretty-printing, then validate the digits. Spaces, dashes, brackets and dots are fine.
const digitsOf = (v) => String(v || '').replace(/[\s\-().]/g, '')

// Mirror of PhoneNormalizable.canonical (app/models/concerns/phone_normalizable.rb). Reception types
// a number any way she likes — with spaces, a leading 0, a 00 prefix — and the field corrects itself
// to the +27 form the server stores and Twilio needs. Same rules, same result, no surprises on save.
const canonicalPhone = (v) => {
  let d = String(v || '').replace(/\D/g, '')
  if (!d) return ''
  d = d.replace(/^00/, '')                    // international 00 prefix
  if (d.startsWith('0')) d = `27${d.slice(1)}` // national leading 0 -> 27
  if (d.length === 9) d = `27${d}`             // bare 9-digit SA number
  return `+${d}`
}
// A landline can never receive WhatsApp — say so the moment she types it, not after a week of
// silent 63024 failures (the Justin Pontus / Visnesh Robilal pattern).
const SA_LANDLINE = /^\+27[1-5]\d{8}$/
const SA_MOBILE = /^\+27[6-8]\d{8}$/
const whatsappWarning = (v) => {
  const c = canonicalPhone(v)
  if (!c) return null
  if (SA_LANDLINE.test(c)) return 'This looks like a landline — WhatsApp cannot deliver to it. Ask the patient for their mobile.'
  if (c.startsWith('+27') && !SA_MOBILE.test(c)) return 'This is not a valid SA mobile number — check the digits.'
  if (!/^\+\d{9,15}$/.test(c)) return 'This does not look like a valid number.'
  return null
}
const phoneRegex = /^\+?\d{9,15}$/

const schema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name:  z.string().min(1, 'Last name is required'),
  // Phone is now optional — family/shared phones are common in SA, and ID
  // number is sufficient identity. Validate only when supplied.
  phone:      z.string()
    .refine((v) => !v || phoneRegex.test(digitsOf(v)), 'Enter a valid number, e.g. 082 123 4567 or +27 82 123 4567')
    .optional(),
  // The number WhatsApp sends to. Blank = same as the phone number.
  whatsapp_number: z.string()
    .refine((v) => !v || phoneRegex.test(digitsOf(v)), 'Enter a valid number, e.g. 082 123 4567 or +27 82 123 4567')
    .optional(),
  email:      z.string().email('Invalid email').or(z.literal('')).optional(),
  date_of_birth: z.string().optional(),
  // SA ID number (13 digits) OR passport. Free-text; validation is loose
  // so receptionist can type-as-they-hear; uniqueness lives on the model.
  id_number:  z.string().optional(),
  notes:      z.string().optional(),
  referral_source: z.string().optional(),
  referral_source_detail: z.string().optional(),

  // ── Billing account (optional) ──
  // If billing_name is supplied we attach a BillingAccount. Otherwise we
  // skip account creation entirely (most "self-pay walk-ins" don't need one).
  account_code:         z.string().optional(),
  account_billing_name: z.string().optional(),
  account_email:        z.string().email('Invalid email').or(z.literal('')).optional(),
  account_phone:        z.string()
    .refine((v) => !v || phoneRegex.test(digitsOf(v)), 'Enter a valid number, e.g. 082 123 4567')
    .optional(),

  // ── Medical aid (optional) ──
  // scheme_id picks from the seeded dropdown; scheme_name_other lets the
  // receptionist type any scheme name not yet in the catalogue.
  scheme_id:          z.string().optional(),
  scheme_name_other:  z.string().optional(),
  membership_number:  z.string().optional(),
  dependant_code:     z.string().optional(),
  plan_option:        z.string().optional(), // medical-aid plan / option (e.g. Classic Saver) — shows on estimate/invoice
  main_member_name:   z.string().optional(),
  // mainmember-1015: "self" = this patient IS the main member · "other" = a dependant of someone else
  // (typed name + ID, optionally a registered patient picked as the main member).
  main_member_mode:      z.enum(['self', 'other']).optional(),
  main_member_id_number: z.string().optional(),
  main_member_patient_id: z.string().optional(),

  // POPIA — Patient has signed the paper AI-processing consent form.
  // The receptionist ticks this AFTER filing the signed form physically.
  // When false: AI summaries, mailbox drafts, scribe outputs all SKIP
  // this patient. Default false.
  ai_consent: z.boolean().optional(),

  // Medical history — all optional.
  mh_allergies:              z.string().optional(),
  mh_chronic_conditions:     z.string().optional(),
  mh_current_medications:    z.string().optional(),
  mh_blood_type:             z.string().optional(),
  mh_emergency_contact_name: z.string().optional(),
  mh_emergency_contact_phone: z.string()
    .refine((v) => !v || phoneRegex.test(digitsOf(v)), 'Enter a valid number, e.g. 082 123 4567')
    .optional(),
  mh_insurance_provider:        z.string().optional(),
  mh_insurance_policy_number:   z.string().optional(),
  mh_dental_notes:              z.string().optional(),
  mh_last_dental_visit:         z.string().optional(),
})

// Default blood types — the server is the authoritative source
// (PatientMedicalHistory::BLOOD_TYPES) but we mirror it here so
// the dropdown still renders if the prop isn't passed (e.g. create
// mode before a patient exists).
const DEFAULT_BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

// "How did you hear about us?" — mirrors Patient::REFERRAL_SOURCES (the server list is authoritative
// and is passed in as `referralSources`; this fallback only keeps the dropdown rendering in create
// mode on pages that don't pass it).
const DEFAULT_REFERRAL_SOURCES = [
  'Google Search', 'Google Maps', 'Practice website', 'Facebook', 'Instagram',
  'Referred by a friend or family member', 'Referred by another dentist, doctor or specialist',
  'Medical aid provider list', 'Outdoor sign / drove past', 'Local community group (WhatsApp or Facebook)',
  'Existing patient returning', 'Other',
]
const REFERRAL_DETAIL_PROMPT = {
  'Referred by a friend or family member': 'Who can we thank for the referral?',
  'Referred by another dentist, doctor or specialist': 'Their name or practice',
  'Other': 'Please tell us where',
}

const EMPTY_DEFAULTS = {
  first_name: '', last_name: '', phone: '', whatsapp_number: '', email: '',
  date_of_birth: '', id_number: '', notes: '',
  referral_source: '', referral_source_detail: '',
  ai_consent: false,
  account_code: '', account_billing_name: '', account_email: '', account_phone: '',
  account_address_line1: '', account_address_line2: '', account_city: '', account_postal_code: '',
  scheme_id: '', scheme_name_other: '', membership_number: '', dependant_code: '',
  plan_option: '', main_member_name: '',
  main_member_mode: 'self', main_member_id_number: '', main_member_patient_id: '',
  mh_allergies: '', mh_chronic_conditions: '', mh_current_medications: '',
  mh_blood_type: '', mh_emergency_contact_name: '', mh_emergency_contact_phone: '',
  mh_insurance_provider: '', mh_insurance_policy_number: '',
  mh_dental_notes: '', mh_last_dental_visit: '',
}

// ── Medical-aid MAIN MEMBER (mainmember-1015 · Paul 2026-10-15: "it is not updating — Henrieta Matilda
// Lyon is the main member!") ──
// The form used to show the main member read-only and the server re-derived it from the account head on
// EVERY save, so a correct dependant setup was reset the next time anyone saved the patient. Reception now
// chooses "This patient" / "Someone else"; the server applies it ONLY when one of these fields was actually
// changed (main_member_touched) — an ordinary save never moves the main member.
const MAIN_MEMBER_FIELDS = ['main_member_mode', 'main_member_name', 'main_member_id_number', 'main_member_patient_id', 'dependant_code']
const mainMemberDefaults = (scheme) => {
  const other = scheme?.main_member_mode === 'other'
  return {
    main_member_mode: other ? 'other' : 'self',
    // "Someone else" fields start EMPTY for a main member, so switching never pre-fills the patient's own
    // name/ID as somebody else's.
    main_member_name: other ? (scheme?.main_member_name || '') : '',
    main_member_id_number: other ? (scheme?.main_member_id_number || '') : '',
    main_member_patient_id: other && scheme?.main_member_patient?.id ? String(scheme.main_member_patient.id) : '',
    dependant_code: scheme?.dependant_code || '',
  }
}

export default function PatientFormModal({
  open,
  onClose,
  mode = 'create',         // 'create' | 'edit'
  patient,                 // required for mode=edit
  accountCode = '',        // current file/account number (edit mode) — editable so reception can re-allocate it
  addressLine1 = '', addressLine2 = '', city = '', postalCode = '', // account address (edit mode pre-fill)
  referralSources,         // Patient::REFERRAL_SOURCES from the server (falls back to the mirror below)
  medicalHistory,          // optional existing medical history hash
  bloodTypes = DEFAULT_BLOOD_TYPES,
  schemes = [],            // [{id, name}, ...] seeded SA medical schemes
  scheme = null,           // the patient's existing scheme membership (edit mode pre-fill)
}) {
  const isEdit = mode === 'edit'
  // The main-member values the form opened with — "touched" means reception changed one of them.
  const hydratedMainMember = useRef(mainMemberDefaults(null))
  // A registered patient picked as the main member (optional) — shown as a chip by PatientSearchSelect.
  const [pickedMainMember, setPickedMainMember] = useState(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_DEFAULTS,
  })

  // Watch scheme_id so we can reveal the "Other" free-text field when the
  // receptionist picks the sentinel "other" option.
  const watchedSchemeId = watch('scheme_id')
  const watchedPhone = watch('phone')
  const watchedWhatsapp = watch('whatsapp_number')
  const watchedReferral = watch('referral_source')
  const referralOptions = (referralSources && referralSources.length) ? referralSources : DEFAULT_REFERRAL_SOURCES
  const waWarning = whatsappWarning(watchedWhatsapp)
  const health = patient?.whatsapp_health
  const isOtherScheme = watchedSchemeId === 'other'
  const watchedMainMemberMode = watch('main_member_mode')
  const mainMemberIsOther = watchedMainMemberMode === 'other'
  const setMainMemberMode = (m) => {
    setValue('main_member_mode', m, { shouldDirty: true })
    // 00 is the main member's own code — a dependant needs theirs (01, 02 …), so don't carry 00 across.
    if (m === 'other' && watch('dependant_code') === '00') setValue('dependant_code', '', { shouldDirty: true })
  }
  const pickMainMember = (r) => {
    setPickedMainMember(r)
    setValue('main_member_patient_id', r ? String(r.id) : '', { shouldDirty: true })
    if (r) {
      setValue('main_member_name', r.name || '', { shouldDirty: true })
      if (r.id_number && !String(r.id_number).startsWith('DEP-')) setValue('main_member_id_number', r.id_number, { shouldDirty: true })
    }
  }

  // Suggest the next free file number in the surname's letter range (e.g. E0042 for "Easton").
  const suggestCode = async () => {
    const surname = watch('last_name')
    if (!surname) { toast.error('Enter a surname first'); return }
    try {
      const res = await fetch(`/patients/next_account_code?surname=${encodeURIComponent(surname)}`, { headers: { Accept: 'application/json' } })
      const d = await res.json()
      if (d.account_code) setValue('account_code', d.account_code, { shouldDirty: true })
    } catch { /* ignore */ }
  }

  // Hydrate form when opening or switching target patient.
  useEffect(() => {
    if (!open) return
    if (isEdit && patient) {
      reset({
        first_name: patient.first_name || '',
        last_name:  patient.last_name  || '',
        phone:      patient.phone      || '',
        whatsapp_number: patient.whatsapp_number || '',
        email:      patient.email      || '',
        date_of_birth: patient.date_of_birth || '',
        id_number:  patient.id_number  || '',
        notes:      patient.notes      || '',
        referral_source: patient.referral_source || '',
        referral_source_detail: patient.referral_source_detail || '',
        ai_consent: !!patient.ai_consent,
        // File/account number IS editable in edit mode (re-allocation). The other account
        // fields (billing name/email/phone) live on the dedicated Account screen.
        account_code: accountCode || '', account_billing_name: '', account_email: '', account_phone: '',
        account_address_line1: addressLine1 || '', account_address_line2: addressLine2 || '',
        account_city: city || '', account_postal_code: postalCode || '',
        // Pre-fill the structured medical scheme so reception sees + edits it on the patient (incl.
        // the PLAN/option that prints on estimates/invoices). Pulled through from the intake form.
        scheme_id: scheme?.scheme_id ? String(scheme.scheme_id) : '',
        scheme_name_other: '',
        membership_number: scheme?.membership_number || '',
        plan_option: scheme?.plan_option || '',
        ...mainMemberDefaults(scheme),   // main_member_mode/name/id/patient + dependant_code
        mh_allergies:              medicalHistory?.allergies || '',
        mh_chronic_conditions:     medicalHistory?.chronic_conditions || '',
        mh_current_medications:    medicalHistory?.current_medications || '',
        mh_blood_type:             medicalHistory?.blood_type || '',
        mh_emergency_contact_name: medicalHistory?.emergency_contact_name || '',
        mh_emergency_contact_phone: medicalHistory?.emergency_contact_phone || '',
        mh_insurance_provider:     medicalHistory?.insurance_provider || '',
        mh_insurance_policy_number: medicalHistory?.insurance_policy_number || '',
        mh_dental_notes:           medicalHistory?.dental_notes || '',
        mh_last_dental_visit:      medicalHistory?.last_dental_visit
          ? medicalHistory.last_dental_visit.slice(0, 10)
          : '',
      })
      hydratedMainMember.current = mainMemberDefaults(scheme)
      setPickedMainMember(scheme?.main_member_mode === 'other' ? (scheme?.main_member_patient || null) : null)
    } else {
      reset(EMPTY_DEFAULTS)
      hydratedMainMember.current = mainMemberDefaults(null)
      setPickedMainMember(null)
    }
  }, [open, isEdit, patient, accountCode, addressLine1, addressLine2, city, postalCode, medicalHistory, scheme, reset])

  const onSubmit = (data) => {
    // Reshape flat form into the nested params shape Rails expects.
    // Empty strings become nulls so they clear fields on update.
    const nullify = (v) => (v === '' || v == null ? null : v)
    const payload = {
      patient: {
        first_name: data.first_name,
        last_name:  data.last_name,
        phone:      nullify(digitsOf(data.phone)),   // send the digits, not the pretty-printing
        whatsapp_number: nullify(canonicalPhone(data.whatsapp_number)),  // blank => NULL => same as phone
        email:      nullify(data.email),
        date_of_birth: nullify(data.date_of_birth),
        id_number:  nullify(data.id_number),
        notes:      nullify(data.notes),
        referral_source: nullify(data.referral_source),
        referral_source_detail: nullify(data.referral_source_detail),
        ai_consent: !!data.ai_consent,
        medical_history_attributes: {
          ...(medicalHistory?.id ? { id: medicalHistory.id } : {}),
          allergies:              nullify(data.mh_allergies),
          chronic_conditions:     nullify(data.mh_chronic_conditions),
          current_medications:    nullify(data.mh_current_medications),
          blood_type:             nullify(data.mh_blood_type),
          emergency_contact_name: nullify(data.mh_emergency_contact_name),
          emergency_contact_phone: nullify(digitsOf(data.mh_emergency_contact_phone)),
          insurance_provider:     nullify(data.mh_insurance_provider),
          insurance_policy_number: nullify(data.mh_insurance_policy_number),
          dental_notes:           nullify(data.mh_dental_notes),
          last_dental_visit:      nullify(data.mh_last_dental_visit),
        },
      },
    }

    // Account & scheme are sent as separate top-level keys (not nested
    // under patient) so PatientsController#create can route them to the
    // attach_account! / attach_scheme! helpers — best-effort, never blocks
    // the patient create.
    if (!isEdit && data.account_billing_name) {
      payload.account = {
        account_code:  nullify(data.account_code),
        billing_name:  data.account_billing_name,
        email:         nullify(data.account_email),
        phone:         nullify(digitsOf(data.account_phone)),
      }
    }
    // EDIT: re-allocate the file/account number AND maintain the account's postal address (so
    // reception can fix a wrong/imported address). Backend renames the account + sets the address.
    // Only sent when a code is present, so we never clear the code.
    if (isEdit && data.account_code) {
      payload.account = {
        account_code: nullify(data.account_code),
        address_line1: nullify(data.account_address_line1),
        address_line2: nullify(data.account_address_line2),
        city:          nullify(data.account_city),
        postal_code:   nullify(data.account_postal_code),
      }
    }
    // Send the structured scheme on CREATE *and* EDIT (Paul 2026-06-19) so the plan/option +
    // membership are captured and pull through to estimates/invoices.
    if (data.membership_number) {
      payload.scheme = {
        scheme_id:         data.scheme_id === 'other' ? null : nullify(data.scheme_id),
        scheme_name:       data.scheme_id === 'other' ? nullify(data.scheme_name_other) : null,
        membership_number: data.membership_number,
        dependant_code:    nullify(data.dependant_code),
        plan_option:       nullify(data.plan_option),
        main_member_name:  nullify(data.main_member_name),
        // mainmember-1015 — "self" | "other" + the main member's own name/ID (+ optional registered patient).
        // The main member's ID goes to the MEMBERSHIP, never to this patient's own ID number.
        main_member_mode:       data.main_member_mode || 'self',
        main_member_id_number:  nullify((data.main_member_id_number || '').replace(/\s+/g, '')),
        main_member_patient_id: nullify(data.main_member_patient_id),
        // Only a CHANGE to the main-member fields may move the main member — never an ordinary save.
        main_member_touched: MAIN_MEMBER_FIELDS.some((k) => String(data[k] ?? '') !== String(hydratedMainMember.current[k] ?? '')),
      }
    }

    const opts = {
      preserveScroll: true,
      preserveState: true,
      onSuccess: (page) => {
        const notice = page?.props?.flash?.notice
        toast.success(notice || (isEdit ? 'Patient updated' : 'Patient created'))
        onClose?.()
      },
      onError: (errs) => {
        const msg = Object.values(errs || {})[0] || 'Something went wrong'
        toast.error(msg)
      },
    }

    if (isEdit) {
      router.patch(`/patients/${patient.id}`, payload, opts)
    } else {
      router.post('/patients', payload, opts)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Patient' : 'New Patient'}
      size="2xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-medium text-brand-muted transition-colors hover:bg-brand-surface/45 hover:text-brand-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="patient-form"
            disabled={isSubmitting}
            className="rounded-2xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-[0_18px_35px_-24px_rgba(49,100,222,0.9)] transition-colors hover:bg-brand-primary-dark disabled:opacity-50"
          >
            {isEdit ? 'Save changes' : 'Create patient'}
          </button>
        </>
      }
    >
      <form id="patient-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* ── Demographics ─────────────────────────────────────── */}
        <Section title="Demographics">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name" error={errors.first_name?.message}>
              <Input {...register('first_name')} />
            </Field>
            <Field label="Last name" error={errors.last_name?.message}>
              <Input {...register('last_name')} />
            </Field>
            <Field label="Phone (optional)" error={errors.phone?.message}>
              <Input
                type="tel"
                placeholder="+27 82 123 4567"
                {...register('phone', {
                  onBlur: (e) => {
                    const c = canonicalPhone(e.target.value)
                    if (c && c !== e.target.value) setValue('phone', c, { shouldDirty: true, shouldValidate: true })
                  },
                })}
              />
            </Field>
            {/* THE WHATSAPP SLOT (Paul 2026-07-17). Two numbers, two slots: the phone above is the
                TELEPHONE number (may be a landline); this is the number WhatsApp actually messages —
                the diary, the reminders and the conversation thread all send here. When reception is
                told "the patient isn't getting our messages, THIS is their WhatsApp number", this is
                the one field she changes, and it pulls through everywhere.
                Blank = same as the phone number, and it then follows any correction to the phone. */}
            <Field label="WhatsApp number (optional)" error={errors.whatsapp_number?.message}>
              <Input
                type="tel"
                placeholder={watchedPhone ? `Same as phone — ${canonicalPhone(watchedPhone) || watchedPhone}` : 'Same as phone number'}
                {...register('whatsapp_number', {
                  onBlur: (e) => {
                    const c = canonicalPhone(e.target.value)
                    if (c && c !== e.target.value) setValue('whatsapp_number', c, { shouldDirty: true, shouldValidate: true })
                  },
                })}
              />
              {waWarning && (
                <p className="mt-1 flex items-start gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-900">
                  <span aria-hidden="true">⚠️</span><span>{waWarning}</span>
                </p>
              )}
              {/* Is WhatsApp actually REACHING them? Shown where she types the number, because this is
                  the field that fixes it. Only RED/AMBER — a green badge on every patient is noise she
                  would learn to ignore, and the whole point is that a warning here MEANS something. */}
              {!waWarning && health && (health.level === 'red' || health.level === 'amber') && (
                <p className={`mt-1 flex items-start gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ${
                  health.level === 'red'
                    ? 'border-red-300 bg-red-50 text-red-800'
                    : 'border-amber-300 bg-amber-50 text-amber-900'
                }`}>
                  <span aria-hidden="true">{health.level === 'red' ? '🚫' : '⚠️'}</span>
                  <span>
                    <strong>{health.level === 'red' ? 'Not reaching this patient.' : 'Possibly not reaching them.'}</strong>{' '}
                    {health.reason}
                  </span>
                </p>
              )}
              <p className="mt-1 text-[11px] text-brand-muted">
                {watchedWhatsapp
                  ? 'WhatsApp messages go to this number.'
                  : `WhatsApp messages go to the phone number${watchedPhone ? ` (${watchedPhone})` : ''}. Only fill this in if the patient WhatsApps from a different number.`}
              </p>
            </Field>
            <Field label="Email" error={errors.email?.message}>
              <Input type="email" {...register('email')} />
            </Field>
            <Field label="Date of birth">
              <Input type="date" {...register('date_of_birth')} />
            </Field>
            <Field label="ID / passport number">
              <Input placeholder="13-digit SA ID or passport" {...register('id_number')} />
            </Field>
          </div>
          {/* "How did you hear about us?" — the intake form asks this; reception can set or correct it here. */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="How did they hear about us?">
              <select
                {...register('referral_source')}
                className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              >
                <option value="">— Not asked —</option>
                {referralOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            {REFERRAL_DETAIL_PROMPT[watchedReferral] && (
              <Field label={REFERRAL_DETAIL_PROMPT[watchedReferral]}>
                <Input placeholder="Name" {...register('referral_source_detail')} />
              </Field>
            )}
          </div>
          {isEdit && (
            <Field label="File / account number" error={errors.account_code?.message}>
              <div className="flex gap-2">
                <Input placeholder="e.g. E0042" {...register('account_code')} />
                <button type="button" onClick={suggestCode}
                  className="shrink-0 rounded-2xl border border-brand-accent/80 px-3 py-2 text-sm font-medium text-brand-primary transition hover:bg-brand-surface">
                  Suggest
                </button>
              </div>
              <p className="mt-1 text-xs text-brand-muted">
                The patient's billing file number. Change it to re-allocate — e.g. an imported code into the surname's letter range (<strong>E</strong> for Easton). Must be unique. <em>Suggest</em> fills the next free number for the surname.
              </p>
            </Field>
          )}
          <Field label="Notes">
            <textarea
              rows={2}
              placeholder="General notes about this patient…"
              {...register('notes')}
              className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
            />
          </Field>
        </Section>

        {/* ── POPIA consent (Paul's 2026-05-24 decision: paper form + this checkbox) ── */}
        <Section title="AI processing consent (POPIA)" subtitle="Tick only after the patient has signed the paper consent form and it's filed.">
          <label className="flex items-start gap-3 rounded-2xl border border-brand-accent/80 bg-brand-surface/30 px-4 py-3">
            <input
              type="checkbox"
              {...register('ai_consent')}
              className="mt-0.5 h-5 w-5 rounded border-brand-accent text-brand-primary focus:ring-brand-primary"
            />
            <span className="text-sm text-brand-ink">
              <strong>Consent to AI processing — paper form on file.</strong>
              <span className="mt-0.5 block text-xs text-brand-muted">
                When ticked, the chair-side scribe summary, mailbox booking drafts, and any future AI features will process this patient's data. When unticked, all AI features SKIP this patient.
              </span>
            </span>
          </label>
        </Section>

        {/* ── Billing account (create only — edit happens on the Account screen) ── */}
        {!isEdit && (
          <Section title="Billing account" subtitle="Optional — leave blank for a self-pay walk-in. Only fill this if a family member or insurer pays.">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Account holder / billing name">
                <Input placeholder="e.g. John Smith (father)" {...register('account_billing_name')} />
              </Field>
              <Field label="Existing account code">
                <Input placeholder="e.g. ACC-0042 (leave blank to auto-generate)" {...register('account_code')} />
              </Field>
              <Field label="Billing email" error={errors.account_email?.message}>
                <Input type="email" {...register('account_email')} />
              </Field>
              <Field label="Billing phone" error={errors.account_phone?.message}>
                <Input type="tel" placeholder="+27 82 123 4567" {...register('account_phone')} />
              </Field>
            </div>
          </Section>
        )}

        {/* ── Account postal address (edit mode, when the patient has an account) — the address that
              prints on statements/correspondence. Editable so reception can correct it. ── */}
        {isEdit && accountCode && (
          <Section title="Account postal address" subtitle="The address on statements & letters. Edit to correct it.">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Address line 1">
                <Input placeholder="e.g. 12 Oak Street / P.O. Box 344" {...register('account_address_line1')} />
              </Field>
              <Field label="Address line 2 / suburb">
                <Input placeholder="e.g. Honeydew" {...register('account_address_line2')} />
              </Field>
              <Field label="City / town">
                <Input placeholder="e.g. Roodepoort" {...register('account_city')} />
              </Field>
              <Field label="Postal code">
                <Input placeholder="e.g. 1724" {...register('account_postal_code')} />
              </Field>
            </div>
          </Section>
        )}

        {/* ── Medical aid (create AND edit) — the single structured scheme record that prints on
              estimates/invoices for the patient's claim. Shown on edit so reception can correct
              what the intake form captured, incl. the PLAN. ── */}
        <Section title="Medical aid" subtitle="Optional — patient claims back themselves. We don't submit claims. Shows on estimates/invoices.">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Scheme">
              <select
                {...register('scheme_id')}
                className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              >
                <option value="">— None / Private —</option>
                {schemes.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
                <option value="other">Other (type below)…</option>
              </select>
            </Field>
            {isOtherScheme && (
              <Field label="Other scheme name">
                <Input placeholder="e.g. Sizwe Hosmed" {...register('scheme_name_other')} />
              </Field>
            )}
            <Field label="Plan / option">
              <Input placeholder="e.g. Classic Saver, KeyCare Plus" {...register('plan_option')} />
            </Field>
            <Field label="Membership number">
              <Input {...register('membership_number')} />
            </Field>
          </div>

          {/* ── Main member (mainmember-1015) — "This patient" or "Someone else". The main member may not be a
                patient here at all (George Lyon is a dependant on his wife's GEMS); their name + ID print on
                estimates/invoices. Applied only when one of these fields is CHANGED — a normal save keeps it. ── */}
          <div className="space-y-3 rounded-2xl border border-brand-accent/60 bg-brand-surface/20 p-4">
            <input type="hidden" {...register('main_member_mode')} />
            <input type="hidden" {...register('main_member_patient_id')} />
            <div>
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Main member</span>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Main member">
                {[['self', 'This patient'], ['other', 'Someone else (this patient is a dependant)']].map(([v, label]) => (
                  <button key={v} type="button" role="radio" aria-checked={watchedMainMemberMode === v}
                    onClick={() => setMainMemberMode(v)}
                    className={`rounded-2xl border px-3 py-2 text-sm font-medium transition-colors ${watchedMainMemberMode === v
                      ? 'border-brand-primary bg-brand-primary text-white'
                      : 'border-brand-accent/80 bg-white text-brand-ink hover:bg-brand-surface/45'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {mainMemberIsOther ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Main member name" error={errors.main_member_name?.message}>
                    <Input placeholder="e.g. the spouse or parent on the card" {...register('main_member_name')} />
                  </Field>
                  <Field label="Main member ID number">
                    <Input placeholder="The MAIN MEMBER's own ID (not this patient's)" {...register('main_member_id_number')} />
                  </Field>
                  <Field label="This patient's dependant code">
                    <Input placeholder="e.g. 01" {...register('dependant_code')} />
                  </Field>
                </div>
                <div>
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Is the main member also our patient? (optional)</span>
                  <PatientSearchSelect selected={pickedMainMember} onSelect={pickMainMember} excludeId={patient?.id} />
                  <p className="mt-1 text-[11px] text-brand-muted">Pick them to link their file. Leave empty if the main member is not a patient here.</p>
                </div>
              </>
            ) : (
              <p className="text-xs text-brand-muted">This patient is the main member on this medical aid (dependant code 00).</p>
            )}
            {isEdit && scheme?.main_member_name && (
              <p className="text-[11px] text-brand-muted">
                Estimates &amp; invoices print now: <strong>Main member {scheme.main_member_name}</strong>
                {scheme.main_member_id_number ? ` · ID ${scheme.main_member_id_number}` : ''}
              </p>
            )}
          </div>
        </Section>

        {/* ── Medical history ──────────────────────────────────── */}
        <Section title="Medical History" subtitle="Optional — fill in what you have">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Allergies">
              <textarea
                rows={2}
                placeholder="e.g. Penicillin, latex"
                {...register('mh_allergies')}
                className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              />
            </Field>
            <Field label="Chronic conditions">
              <textarea
                rows={2}
                placeholder="e.g. Hypertension, diabetes"
                {...register('mh_chronic_conditions')}
                className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              />
            </Field>
            <Field label="Current medications">
              <textarea
                rows={2}
                {...register('mh_current_medications')}
                className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              />
            </Field>
            <Field label="Blood type">
              <select
                {...register('mh_blood_type')}
                className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              >
                <option value="">Unknown</option>
                {bloodTypes.map((bt) => (
                  <option key={bt} value={bt}>{bt}</option>
                ))}
              </select>
            </Field>
            <Field label="Emergency contact name">
              <Input {...register('mh_emergency_contact_name')} />
            </Field>
            <Field label="Emergency contact phone" error={errors.mh_emergency_contact_phone?.message}>
              <Input type="tel" placeholder="+27 82 123 4567" {...register('mh_emergency_contact_phone')} />
            </Field>
            <Field label="Medical aid provider (for claim-back)">
              <Input {...register('mh_insurance_provider')} />
            </Field>
            <Field label="Medical aid policy number">
              <Input {...register('mh_insurance_policy_number')} />
            </Field>
            <Field label="Last dental visit">
              <Input type="date" {...register('mh_last_dental_visit')} />
            </Field>
          </div>
          <Field label="Dental notes">
            <textarea
            rows={2}
            placeholder="Prior procedures, sensitivities, anxiety triggers…"
            {...register('mh_dental_notes')}
            className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
          />
        </Field>
        </Section>
      </form>
    </Modal>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-3 border-b border-brand-accent/60 pb-2">
        <h3 className="text-sm font-semibold text-brand-ink">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-brand-muted">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-brand-danger">{error}</span>}
    </label>
  )
}

const Input = React.forwardRef(function Input(props, ref) {
  return (
    <input
      ref={ref}
      {...props}
      className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
    />
  )
})
